import { Bolt, DeleteOutline, EscalatorWarning, Edit, Face5, Person, PersonAddAlt, PersonAddAlt1 } from "@mui/icons-material";
import TooltipComponent from "components/TooltipComponent";
import { t } from "i18next";
import { ReactNode } from "react";
import { IRecognitionTypeOption, IRecognitionTypes } from "services/Recognition/types";
import { Permissions } from "services/enums";

export const getRecognitionTypeLabel = (
    type: IRecognitionTypes,
  ): React.ReactNode => {
    switch (type) {
      case IRecognitionTypes.GENERAL:
        return (
          <TooltipComponent title={t('recognitionPage.registerFace.general')}>
            <Person color={'primary'} />
          </TooltipComponent>
        );
      case IRecognitionTypes.CUSTOMER:
        return (
          <TooltipComponent title={t('recognitionPage.registerFace.customer')}>
            <PersonAddAlt color={'warning'} />
          </TooltipComponent>
        );
      case IRecognitionTypes.STUDENT:
        return (
          <TooltipComponent title={t('recognitionPage.registerFace.student')}>
            <Face5 color={'success'} />
          </TooltipComponent>
        );
      case IRecognitionTypes.RESPONSIBLE:
        return (
          <TooltipComponent
            title={t('recognitionPage.registerFace.responsible')}
          >
            <EscalatorWarning color={'secondary'} />
          </TooltipComponent>
        );
      case IRecognitionTypes.QUICK_RELEASE:
        return (
          <TooltipComponent title={t('recognitionPage.registerFace.quickRelease')}>
            <Bolt sx={{ color: 'grey.500' }} />
          </TooltipComponent>
        );
      default:
        return 'Desconhecido';
    }
  };

export const getRecognitionTypesOptions = (): IRecognitionTypeOption[] => [
    { label: t('recognitionPage.modalEditFace.typesOfResponsible.general'), value: IRecognitionTypes.GENERAL },
    { label: t('recognitionPage.modalEditFace.typesOfResponsible.customer'), value: IRecognitionTypes.CUSTOMER },
    { label: t('recognitionPage.modalEditFace.typesOfResponsible.student'), value: IRecognitionTypes.STUDENT },
    { label: t('recognitionPage.modalEditFace.typesOfResponsible.responsible'), value: IRecognitionTypes.RESPONSIBLE },
  ];

/**
 * Tipos entre os quais a troca de tipo e permitida. Espelha ChangeableTypes do
 * backend: Cliente fica de fora.
 */
export const CHANGEABLE_RECOGNITION_TYPES: IRecognitionTypes[] = [
  IRecognitionTypes.GENERAL,
  IRecognitionTypes.STUDENT,
  IRecognitionTypes.RESPONSIBLE,
];

/** Permissao de update exigida por tipo, mesma matriz que o backend checa em EnsureHasUpdatePermissionForType. */
export const RECOGNITION_UPDATE_PERMISSION_BY_TYPE: Partial<
  Record<IRecognitionTypes, Permissions>
> = {
  [IRecognitionTypes.GENERAL]: Permissions.GENERAL_RECOGNITION_UPDATE,
  [IRecognitionTypes.STUDENT]: Permissions.STUDENT_RECOGNITION_UPDATE,
  [IRecognitionTypes.RESPONSIBLE]: Permissions.RESPONSIBLE_RECOGNITION_UPDATE,
};

/**
 * Chaves i18n dos avisos do que se perde numa troca de tipo, conforme a
 * transicao. Espelha as regras de RecognitionTypeChangeRules do backend.
 */
export const getRecognitionTypeChangeWarnings = (
  from: IRecognitionTypes,
  to: IRecognitionTypes,
): string[] => {
  const warnings: string[] = [];

  if (from === IRecognitionTypes.STUDENT) {
    warnings.push('recognitionPage.changeType.warnings.studentLinks');
  }

  if (from === IRecognitionTypes.RESPONSIBLE) {
    warnings.push('recognitionPage.changeType.warnings.responsibleAccess');
    warnings.push('recognitionPage.changeType.warnings.responsibleLinks');
  }

  if (
    to === IRecognitionTypes.STUDENT &&
    (from === IRecognitionTypes.GENERAL ||
      from === IRecognitionTypes.RESPONSIBLE)
  ) {
    warnings.push('recognitionPage.changeType.warnings.carPlates');
    warnings.push('recognitionPage.changeType.warnings.vehicleTags');
  }

  return warnings;
};

export const getRecognitionTypeLabelText = (type: IRecognitionTypes): string =>
  getRecognitionTypesOptions().find((option) => option.value === type)
    ?.label ?? type;

export type SyncOperationKind = 'create' | 'update' | 'delete';

export const getSyncOperationFromJobType = (jobType?: string | null): SyncOperationKind => {
  if (jobType?.includes('.delete_')) return 'delete';
  if (jobType?.includes('.update_')) return 'update';
  return 'create';
};

interface SyncOperationMeta {
  label: string;
  preparingMessage: string;
  doneMessage: string;
  icon: ReactNode;
  color: string;
}

export const SYNC_OPERATION_META: Record<SyncOperationKind, SyncOperationMeta> = {
  create: {
    label: 'Cadastro',
    preparingMessage: 'Salvando cadastro e enviando imagem...',
    doneMessage: 'Cadastro concluído!',
    icon: <PersonAddAlt1 />,
    color: 'primary.main',
  },
  update: {
    label: 'Atualização',
    preparingMessage: 'Salvando alterações...',
    doneMessage: 'Atualização concluída!',
    icon: <Edit />,
    color: 'info.main',
  },
  delete: {
    label: 'Exclusão',
    preparingMessage: 'Preparando exclusão...',
    doneMessage: 'Exclusão concluída!',
    icon: <DeleteOutline />,
    color: 'error.main',
  },
};